import { UiOption } from '@/lib/helpers';
import { PolicyModel } from './policy.model';
import { McpToolModel } from '../mcp-tool/mcp-tool.model';
import { orderBy } from 'lodash';

export type McpAction = 'read' | 'read-write';

export type McpBlockAction = 'ignore' | 'log' | 'delete';

export type McpToolAccessAction = 'allow' | 'block';

export type McpEntity = {
  action: McpToolAccessAction;
  toolId: string;
};

export class McpControlConfig {
  action?: McpAction;
  blockAction?: McpBlockAction;
  toolAccess?: McpEntity[];

  constructor(props: Record<string, unknown>) {
    const data = (props || {}) as Record<keyof McpControlConfig, unknown>;

    if (data.action) {
      this.action = data.action as McpAction;
    }

    if (data.blockAction) {
      this.blockAction = data.blockAction as McpBlockAction;
    }

    if (data.toolAccess) {
      this.toolAccess = data.toolAccess as McpEntity[];
    }
  }
}

export const McpActionOptions: UiOption[] = [
  {
    label: 'Read-only',
    value: 'read',
    description:
      'Tools that are able to read resources will be allowed. Tools that can create, update and make other changes will have the Catch-all Action applied.',
  },
  {
    label: 'All Capabilities',
    value: 'read-write',
    description:
      'All tools will have the Catch-all Action applied, regardless of their capabilities.',
  },
];

export const McpBlockActionOptions: UiOption[] = [
  { label: 'Allow', value: 'allow', meta: { header: true } },
  { label: 'Allow', value: 'ignore' },
  { label: 'Monitor', value: 'log' },
  { label: 'Disallow', value: 'disallow', meta: { header: true } },
  { label: 'Block', value: 'delete' },
];

type McpService = {
  name: string;
  id: string;
  providerId: string;
};

export const McpServices: McpService[] = [
  { name: 'CloudFlare Documentation MCP Server', id: 'cf-doc-server', providerId: 'cloudflare' },
  {
    name: 'CloudFlare Workers Bindings MCP Server',
    id: 'cf-workers-bindings-server',
    providerId: 'cloudflare',
  },
  { name: 'CloudFlare Radar MCP Server', id: 'cf-radar-server', providerId: 'cloudflare' },
  { name: 'AWS CloudFormation MCP Server', id: 'aws-cfn-server', providerId: 'aws' },
  { name: 'AWS Documentation MCP Server', id: 'aws-doc-server', providerId: 'aws' },
  { name: 'AWS CDK MCP', id: 'aws-cdk-server', providerId: 'aws' },
  { name: 'AWS Terraform MCP Server', id: 'aws-terraform-server', providerId: 'aws' },
  { name: 'GitHub MCP Server', id: 'github-server', providerId: 'github' },
  { name: 'Azure MCP Server', id: 'azure-server', providerId: 'azure' },
];

export const getMcpActionOptions = (orgPolicy?: PolicyModel): UiOption[] => {
  const actionOptions: UiOption[] = [];
  const orgAction = orgPolicy?.mcpControls?.action || '';
  const hasAllowAll = orgAction === 'read-write';

  McpActionOptions.forEach(({ value, label, description }) => {
    const niceOpt: UiOption = {
      value,
      label,
      description,
    };

    if (value === 'read' && hasAllowAll) {
      niceOpt.meta = { blockedByOrgPolicy: true };
      niceOpt.disabled = true;
    } else if (value === orgAction) {
      niceOpt.meta = { orgPolicyLevel: true };
    }

    actionOptions.push(niceOpt);
  });

  return actionOptions;
};

export const getMcpBlockActionOptions = (orgPolicy?: PolicyModel): UiOption[] => {
  const blockActionOptions: UiOption[] = [];
  const orgBlockAction = orgPolicy?.mcpControls?.blockAction;
  let allowed: McpBlockAction[] = ['ignore', 'log', 'delete'];

  if (orgBlockAction === 'log') {
    allowed = ['ignore', 'log'];
  } else if (orgBlockAction === 'ignore') {
    allowed = ['ignore'];
  }

  McpBlockActionOptions.forEach(({ value, label, meta }) => {
    const niceOpt: UiOption = {
      value,
      label,
      meta,
    };

    if (meta?.header) {
      blockActionOptions.push(niceOpt);
      return;
    }

    const allowedOpt = allowed.includes(value as McpBlockAction);
    const orgOpt = value === orgBlockAction;

    if (orgOpt) {
      niceOpt.meta = { orgPolicyLevel: true };
    } else if (!allowedOpt) {
      niceOpt.disabled = true;
      niceOpt.meta = { blockedByOrgPolicy: true };
    }

    blockActionOptions.push(niceOpt);
  });

  return blockActionOptions;
};

export const getMcpToolOptions = (
  mcpTools: McpToolModel[],
  aclVariant: 'allow' | 'block',
  policy: PolicyModel
): UiOption[] => {
  const toolAccess = policy.mcpControls?.toolAccess || [];
  const alternateVariant = aclVariant === 'allow' ? 'block' : 'allow';
  const alternateList = toolAccess
    .filter(({ action }) => action === alternateVariant)
    .map(({ toolId }) => toolId);

  const opts = mcpTools.map((mcpTool) => {
    const { id, name } = mcpTool;

    const disabledByAlternateList = alternateList.includes(id);

    return {
      label: name,
      value: id,
      meta: { disabledByAlternateList, mcpTool },
    };
  });

  return orderBy(opts, [({ meta }) => meta.mcpTool.serverName.toLowerCase()]);
};
